(() => {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];
  const routes = window.Atelier?.routes || {};
  let lastFocused = null;
  const overlayTimers = new WeakMap();

  const setLiveMessage = (message) => {
    const region = $('[data-live-region]');
    if (region) region.textContent = message;
  };

  const openOverlay = (element) => {
    if (!element) return;
    clearTimeout(overlayTimers.get(element));
    lastFocused = document.activeElement;
    element.inert = false;
    element.setAttribute('aria-hidden', 'false');
    element.classList.add('is-open');
    document.body.classList.add('is-locked');
    overlayTimers.set(element, setTimeout(() => {
      if (element.classList.contains('is-open')) $('button, a, input, select, textarea', element)?.focus({ preventScroll: true });
    }, 80));
  };

  const closeOverlay = (element) => {
    if (!element) return;
    clearTimeout(overlayTimers.get(element));
    element.classList.remove('is-open');
    element.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    overlayTimers.set(element, setTimeout(() => {
      if (!element.classList.contains('is-open')) element.inert = true;
    }, 480));
    lastFocused?.focus?.();
  };

  const formatMoney = (cents, format = window.Atelier?.money || '${{amount}}') => {
    const value = Number(cents || 0) / 100;
    const token = format.match(/\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*\}\}/)?.[1];
    if (!token) return value.toFixed(2);
    const noDecimals = token.includes('no_decimals');
    const comma = token.includes('comma_separator');
    const number = value.toLocaleString(comma ? 'de-DE' : 'en-US', { minimumFractionDigits: noDecimals ? 0 : 2, maximumFractionDigits: noDecimals ? 0 : 2 });
    return format.replace(/\{\{\s*amount[^}]*\}\}/, number);
  };

  const replaceCart = (html) => {
    if (!html) return null;
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const next = $('[data-cart-drawer]', holder);
    const current = $('[data-cart-drawer]');
    if (next && current) current.replaceWith(next);
    return $('[data-cart-drawer]');
  };

  class StoreHeader extends HTMLElement {
    connectedCallback() {
      this.menu = $('[data-menu]', this);
      $('[data-menu-open]', this)?.addEventListener('click', () => openOverlay(this.menu));
      $('[data-menu-close]', this)?.addEventListener('click', () => closeOverlay(this.menu));
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.input = $('input', this);
      $('[data-minus]', this)?.addEventListener('click', () => this.change(-1));
      $('[data-plus]', this)?.addEventListener('click', () => this.change(1));
    }
    change(delta) {
      const min = Number(this.input.min || 0);
      const max = Number(this.input.max || Infinity);
      this.input.value = Math.min(max, Math.max(min, Number(this.input.value || 0) + delta));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form || this.form.dataset.bound) return;
      this.form.dataset.bound = 'true';
      this.form.addEventListener('submit', (event) => this.submit(event));
    }
    async submit(event) {
      event.preventDefault();
      const button = $('[type="submit"]', this.form);
      if (!button || button.disabled) return;
      button.disabled = true;
      button.classList.add('is-loading');
      const data = new FormData(this.form);
      data.append('sections', 'cart-drawer');
      data.append('sections_url', window.location.pathname);
      try {
        const response = await fetch(`${routes.add || '/cart/add'}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
        const drawer = replaceCart(result.sections?.['cart-drawer']);
        openOverlay(drawer);
        const cart = await fetch(`${routes.cart || '/cart'}.js`).then((res) => res.json());
        $$('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
        setLiveMessage(`${result.product_title || 'Item'} added to your bag.`);
      } catch (error) {
        setLiveMessage(error.message);
        alert(error.message);
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]');
      this.variants = JSON.parse($('[data-product-variants]', this)?.textContent || '[]');
      $$('input[type="radio"]', this).forEach((input) => input.addEventListener('change', () => this.update()));
      this.update(false);
    }
    update(changeUrl = true) {
      const options = $$('fieldset', this).map((field) => $('input:checked', field)?.value);
      const variant = this.variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant) {
        const button = $('[data-add-button]', this.root);
        if (button) button.disabled = true;
        const label = $('[data-add-label]', this.root);
        if (label) label.textContent = window.Atelier.strings.unavailable;
        const sticky = $('[data-sticky-add] button');
        if (sticky) sticky.disabled = true;
        return;
      }
      $$('[data-variant-id]', this.root).forEach((input) => { input.value = variant.id; });
      const button = $('[data-add-button]', this.root);
      const label = $('[data-add-label]', this.root);
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? window.Atelier.strings.add : window.Atelier.strings.sold;
      const stickyButton = $('[data-sticky-add] button', this.root.parentElement);
      if (stickyButton) stickyButton.disabled = !variant.available;
      const stock = $('[data-product-stock]', this.root);
      if (stock) {
        const quantity = Number(variant.inventory_quantity);
        stock.classList.toggle('is-low', variant.available && Number.isFinite(quantity) && quantity > 0 && quantity <= 5);
        const copy = $('span', stock);
        if (copy) copy.textContent = variant.available ? (Number.isFinite(quantity) && quantity > 0 && quantity <= 5 ? `Only ${quantity} left in this edit` : 'In stock · Ready to dispatch') : 'Currently unavailable';
      }
      const money = formatMoney(variant.price);
      const displayedPrice = $('[data-price]', this.root);
      if (displayedPrice) {
        displayedPrice.replaceChildren();
        displayedPrice.classList.toggle('price--sale', variant.compare_at_price > variant.price);
        if (variant.compare_at_price > variant.price) {
          const compare = document.createElement('s');
          compare.textContent = formatMoney(variant.compare_at_price).replace(/<[^>]*>/g, '');
          displayedPrice.append(compare);
        }
        const amount = document.createElement('span');
        amount.textContent = money.replace(/<[^>]*>/g, '');
        displayedPrice.append(amount);
      }
      $$('[data-add-price], [data-sticky-price]', this.root.parentElement).forEach((node) => { node.textContent = money; });
      if (variant.featured_media?.id) this.root.querySelector(`product-gallery`)?.selectMedia(variant.featured_media.id);
      if (changeUrl && history.replaceState) history.replaceState({}, '', `${window.location.pathname}?variant=${variant.id}`);
    }
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      $$('[data-media-target]', this).forEach((button) => button.addEventListener('click', () => this.selectMedia(button.dataset.mediaTarget)));
    }
    selectMedia(id) {
      $$('[data-media-id]', this).forEach((media) => media.classList.toggle('is-active', String(media.dataset.mediaId) === String(id)));
      $$('[data-media-target]', this).forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.mediaTarget) === String(id)));
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      fetch(this.dataset.url).then((response) => response.text()).then((text) => {
        const holder = document.createElement('div');
        holder.innerHTML = text;
        const content = $('product-recommendations', holder);
        if (content?.innerHTML.trim()) this.innerHTML = content.innerHTML;
      }).catch(() => {});
    }
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-predictive-input]', this);
      this.results = $('[data-predictive-results]', this);
      this.timer = null;
      this.controller = null;
      this.input?.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 250);
      });
    }
    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) { this.results.innerHTML = ''; return; }
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const url = `${routes.suggest || '/search/suggest'}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&section_id=predictive-search`;
        const text = await fetch(url, { signal: this.controller.signal }).then((response) => response.text());
        const holder = document.createElement('div');
        holder.innerHTML = text;
        this.results.innerHTML = $('[data-predictive-content]', holder)?.outerHTML || '';
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }

  class FragranceFinder extends HTMLElement {
    connectedCallback() {
      this.answers = {};
      this.cards = $$('[data-mood]', this);
      this.status = $('[data-finder-status]', this);
      $$('[data-finder-question]', this).forEach((question) => {
        $$('[data-finder-answer]', question).forEach((button) => button.addEventListener('click', () => this.choose(question, button)));
      });
      $('[data-finder-reset]', this)?.addEventListener('click', () => this.reset());
      this.cards.forEach((card) => { card.hidden = true; });
    }
    choose(question, button) {
      const key = question.dataset.finderQuestion;
      this.answers[key] = button.dataset.finderAnswer;
      $$('[data-finder-answer]', question).forEach((item) => item.classList.toggle('is-selected', item === button));
      this.update();
    }
    update() {
      if (Object.keys(this.answers).length < 3) {
        const left = 3 - Object.keys(this.answers).length;
        this.cards.forEach((card) => { card.hidden = true; });
        if (this.status) this.status.textContent = `${left} choice${left === 1 ? '' : 's'} remaining.`;
        return;
      }
      const scored = this.cards.map((card) => {
        const values = [card.dataset.mood, card.dataset.family, card.dataset.intensity];
        const picks = [this.answers.mood, this.answers.family, this.answers.intensity];
        const all = values.every((value) => value === 'all');
        return { card, score: all ? 0 : values.reduce((score, value, index) => score + (value === picks[index] ? 1 : 0), 0), exact: all || values.every((value, index) => value === picks[index]) };
      });
      let matches = scored.filter((item) => item.exact);
      if (!matches.length && scored.length) {
        const best = Math.max(...scored.map((item) => item.score));
        matches = scored.filter((item) => item.score === best).slice(0, 2);
      }
      this.cards.forEach((card) => { card.hidden = !matches.some((item) => item.card === card); });
      if (this.status) this.status.textContent = matches.length === 1 ? 'One considered match, selected for you.' : `${matches.length} considered matches, selected for you.`;
    }
    reset() {
      this.answers = {};
      $$('[data-finder-answer]', this).forEach((button) => button.classList.remove('is-selected'));
      this.cards.forEach((card) => { card.hidden = true; });
      if (this.status) this.status.textContent = 'Choose one answer from each line.';
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (!customElements.get('store-header')) customElements.define('store-header', StoreHeader);
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
  if (!customElements.get('variant-selector')) customElements.define('variant-selector', VariantSelector);
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  if (!customElements.get('fragrance-finder')) customElements.define('fragrance-finder', FragranceFinder);

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-open]')) openOverlay($('[data-cart-drawer]'));
    if (event.target.closest('[data-cart-close]')) closeOverlay($('[data-cart-drawer]'));
    if (event.target.closest('[data-search-open]')) openOverlay($('[data-search-drawer]'));
    if (event.target.closest('[data-search-close]')) closeOverlay($('[data-search-drawer]'));
    if (event.target.closest('[data-filter-open]')) openOverlay($('[data-filter-drawer]'));
    if (event.target.closest('[data-filter-close]')) closeOverlay($('[data-filter-drawer]'));
    const sticky = event.target.closest('[data-submit-product]');
    if (sticky) document.getElementById(sticky.dataset.submitProduct)?.requestSubmit();
  });

  document.addEventListener('change', async (event) => {
    if (event.target.matches('[data-localization]')) event.target.form?.submit();
    if (event.target.matches('[data-sort-select]')) event.target.form?.submit();
    if (!event.target.matches('[data-cart-quantity]')) return;
    const input = event.target;
    input.disabled = true;
    try {
      const response = await fetch(`${routes.change || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(input.dataset.line), quantity: Number(input.value), sections: ['cart-drawer'], sections_url: window.location.pathname })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update your bag.');
      const drawer = replaceCart(cart.sections?.['cart-drawer']);
      $$('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
      openOverlay(drawer);
    } catch (error) {
      setLiveMessage(error.message);
      input.disabled = false;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const overlay = $('.is-open[aria-hidden="false"]');
      if (overlay) {
        const focusable = $$('button:not([disabled]), a[href], input:not([disabled]), select, textarea', overlay).filter(el => el.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    if (event.key !== 'Escape') return;
    $$('.is-open').forEach(closeOverlay);
  });

  const reveals = $$('.reveal');
  if ('IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries, observer) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -8% 0px' });
    reveals.forEach((element) => revealObserver.observe(element));
  } else reveals.forEach((element) => element.classList.add('is-revealed'));

  const mainAdd = $('[data-add-button]');
  const stickyAdd = $('[data-sticky-add]');
  if (mainAdd && stickyAdd && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => stickyAdd.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 }).observe(mainAdd);
  }
})();

{const f=document.querySelector('.hs-taj-collection [data-filter-drawer]');if(f&&matchMedia('(min-width:990px)').matches){f.removeAttribute('inert');f.setAttribute('aria-hidden','false');}}

document.addEventListener('click',e=>{const b=e.target.closest('[data-taj-columns]');if(b)document.querySelector('.hs-taj-collection .product-grid')?.style.setProperty('--taj-columns',b.dataset.tajColumns);});
